import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CommonLogMessages, LogService } from '../services/log.service';
import { isGuestSession } from '../auth/guest-session';
import { isShareRequestAccess } from '../auth/share-request-access';

@Injectable({
  providedIn: 'root',
})
export class NoAuthGuard {
  constructor(
    private readonly route: Router,
    public auth: AuthService,
    private readonly logService: LogService
  ) {}

  /**
   * Validate if user is not logged in. If user is logged in, then redirect to Main layout
   * @param route
   * @param state
   * @returns {boolean}
   */
  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // SSO/IAM callback routes are mid-authentication: loginWithToken sets the legacy token in
    // localStorage before the callback finishes, so the user momentarily looks "authenticated"
    // here. Redirecting them to /projects causes a flicker before the callback's own post-login
    // navigation runs. Let these routes through and let the callback navigate.
    if (state.url.startsWith('/entry/auth') || state.url.startsWith('/entry/sso')) {
      return true;
    }
    // A share-link guest has no place in the app (`guestConfinementGuard` sends them here), but their
    // session must outlive this screen: Back returns them to the share link without signing in again.
    // So neither the "already signed in" redirect nor the stale-session clean-up below applies.
    if (isGuestSession()) {
      return true;
    }
    if (this.isShareRequestAccessPage(route, state)) {
      return true;
    }
    if (this.auth.getAuthUser() !== null && this.auth.getAuthToken() !== null && !this.auth.isTokenExpired()) {
      this.route.navigate(['/projects']);
      return false;
    }
    // The post-activation verify step is mid-registration: the IAM SDK has already minted
    // `app_token`, but the legacy `auth_user` is not hydrated until the post-verification
    // exchange, so the stale-session cleanup below would POST /api/v1/auth/logout and wipe
    // the brand-new session. Skip cleanup there; the fully-authenticated redirect above still applies.
    // The multi-tenant picker is mid-login too: IAM has already persisted its identity
    // (`iam_auth_user`, session, settings) and only the tenant is still missing. Wiping storage here
    // leaves that identity in SDK memory alone, so after a reload `currentUser()` is null and
    // Drive loses `accountUuid` (edit permissions). The login page already ran this cleanup.
    if (
      !state.url.startsWith('/entry/auth') &&
      !state.url.startsWith('/entry/on-boarding/email-verification') &&
      !state.url.startsWith('/entry/tenant-select')
    ) {
      const extraInfo: any = {
        cause: 'NoAuthGuard',
        msg: '',
        url: state.url,
        username: this.auth.getAuthUserName(),
      };

      if (!this.auth.getAuthUser()) {
        extraInfo.msg += 'no auth user;';
      }
      if (!this.auth.getAuthToken()) {
        extraInfo.msg += 'no auth token;';
      }
      if (this.auth.isTokenExpired()) {
        extraInfo.msg += 'token expired;';
      }

      if (!state.url.includes('/entry/login')) {
        this.logService.logMessage('warning', CommonLogMessages.AutoLogout, extraInfo);
      }
      this.auth.logout({ message: extraInfo });
    }
    return true;
  }

  /**
   * A member refused by a share link is sent to its request-access form still signed in
   * (`GuestShareLogoutService`): they ask the sharer for access from there, so neither the
   * "already signed in" redirect nor the stale-session clean-up applies.
   */
  private isShareRequestAccessPage(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return state.url.startsWith('/entry/login') && isShareRequestAccess(route.queryParamMap);
  }
}
